import { useEffect, useRef, useState } from 'react'
import arrowdown from '@features/projects/assets/arrow-down.svg';
import arrowup from "@features/assignments/assets/arrow-up.svg";
import assignmentsStyles from '@features/assignments/styles/assignment.module.scss';
import { AssignmentCalendarBody } from './AssignmentCalendarBody';
import { AssignmentCalendarTask } from './AssignmentCalendarTask';
import { Assignment, IListTimeAssignmentsResponse } from '@/api/timeAssignments/types';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useSaveEstimatedTimes } from '../../hooks/timeAssignments/useSaveEstimatedTimes';
import { useAssignmentValidation } from '../../hooks/timeAssignments/useAssignmentValidation';
import { useRegisterTimeAssignment } from '../../hooks/timeAssignments/useRegisterTimeAssignment';
import { toast } from 'react-toastify';
import { useDeleteTimeAssignment } from '../../hooks/timeAssignments/useDeleteTimeAssignment';
import { hasEstimatedChanges, hasRealTimeChanges } from '../../utils/assignmentUtils';
import { AddTask } from '@/features/projects/components/projectPage/components/Task/AddTask';
import { useGetAllProjects } from '@/features/projects/hooks/useGetAllProjects';
import { useGetAllSections } from '@/features/projects/hooks/useGetAllSections';
import { useCreateTask } from '@/features/projects/hooks/tasks/useCreateTask';
import { IFormData, ITask } from '@/api/tasks/types';
import { useCreateAssignment } from '../../hooks/useCreateAssignment';
import { handleAxiosError } from '@/api/common/handleAxiosError';

interface AssignmentItemProps {
  item: IListTimeAssignmentsResponse;
  getInfo: () => void;
  week: string[];
  // allUsers: any[];
  actionShowTaskDetail: (item: IListTimeAssignmentsResponse, task: Assignment) => void;
}

export const AssignmentItem = ({
  item,
  getInfo,
  week,
  actionShowTaskDetail,
}: AssignmentItemProps) => {
  const { saveEstimatedTimes } = useSaveEstimatedTimes(getInfo);
  const { mutateAsync: registerTimeAssignment } = useRegisterTimeAssignment();
  const { data: projects } = useGetAllProjects();
  const { data: sections } = useGetAllSections();
  const { mutateAsync: createTask } = useCreateTask();
  const { mutateAsync: createAssignment } = useCreateAssignment();
  const { user } = useAuth();
  const { role, username } = user?.data || {};
  const [active, setActive] = useState<boolean>(false);
  const [style, setStyle] = useState<string>(assignmentsStyles["assignment-item__hide-div"]);
  const [arrow, setArrow] = useState<string>(arrowdown);
  const [isEstimatedDisabled, setIsEstimatedDisabled] = useState<boolean>(true);
  const [isRealTimeDisabled, setIsRealTimeDisabled] = useState<boolean>(true);
  const [editedEstimatedTimes, setEditedEstimatedTimes] = useState<{
    [taskId: string]: { [date: string]: number }
  }>({});
  const [editedRealTimes, setEditedRealTimes] = useState<{ [scheduleId: string]: number }>({});
  const { validateScheduleChange, validateHourInput } = useAssignmentValidation({ week, item, editedEstimatedTimes });
  const { mutateAsync: deleteTimeAssignment } = useDeleteTimeAssignment();
  const [addTaskIcon, setAddTaskIcon] = useState<boolean>(false);
  const [style2, setStyle2] = useState<string>(assignmentsStyles["assignment-item__hide"]);
  const addTaskRef = useRef<{ resetForm: () => void }>(null);
  // const [move, setmove] = useState<number>(0);
  // const [itemloader, setItemLoader] = useState<boolean>(false);
  // const [originalItem, setOriginalItem] = useState<AssignmentItemProps['item']>({ ...item });
  const [openDescription, setOpenDescription] = useState<boolean>(false);
  // const userItem = { fullName: item.fullName, username: item.username };

  useEffect(() => {
    // Obtén todos los ids de schedules actuales
    const existingScheduleIds = new Set<string>();
    item.assignments.forEach(assignment => {
      assignment.schedules.forEach(schedule => {
        existingScheduleIds.add(schedule.id);
      });
    });

    // Limpia editedRealTimes de ids que ya no existen
    setEditedRealTimes(prev => {
      const cleaned: typeof prev = {};
      for (const [id, value] of Object.entries(prev)) {
        if (existingScheduleIds.has(id)) {
          cleaned[id] = value;
        }
      }
      return cleaned;
    });

    // Limpia también los estimados si quieres máxima limpieza
    setEditedEstimatedTimes({});
  }, [item]);

  const handleEstimatedChange = (taskAssignmentId: string, date: string, value: number) => {
    setEditedEstimatedTimes(prev => ({
      ...prev,
      [taskAssignmentId]: {
        ...prev[taskAssignmentId],
        [date]: value
      }
    }));
  };

  const canEditEstimated = role === "ADMIN";
  const canEditReal = role === "ADMIN" || username === item?.username;

  const handleEstimatedToggle = () => {
    setIsEstimatedDisabled(!isEstimatedDisabled);
    setIsRealTimeDisabled(!isRealTimeDisabled);
    if (style === assignmentsStyles["assignment-item__hide-div"]) {
      setStyle(assignmentsStyles["assignment-item__show"]);
      setArrow(arrowup);
    }
  };

  const removeZeroEstimatedSchedules = async () => {
    for (const [taskAssignmentId, dates] of Object.entries(editedEstimatedTimes)) {
      const assignment = item.assignments.find(a => a.task_assignment_id === taskAssignmentId);
      if (!assignment) continue;
      for (const [date, value] of Object.entries(dates)) {
        const schedule = assignment.schedules.find(s => s.scheduleDate.substring(0, 10) === date);
        if (schedule && (Number(value) === 0 || value === null)) {
          await toast.promise(
            deleteTimeAssignment(schedule.id),
            {
              pending: "Eliminando asignación de tiempo...",
              success: "Asignación de tiempo eliminada exitosamente.",
              error: "Error al eliminar la asignación de tiempo."
            }
          );
          getInfo();
        }
      }
    }
  };

  const getFilteredEstimatedTimes = () => {
    const filtered: typeof editedEstimatedTimes = {};
    for (const [taskAssignmentId, dates] of Object.entries(editedEstimatedTimes)) {
      for (const [date, value] of Object.entries(dates)) {
        if (Number(value) > 0) {
          if (!filtered[taskAssignmentId]) filtered[taskAssignmentId] = {};
          filtered[taskAssignmentId][date] = value;
        }
      }
    }
    return filtered;
  };

  const handleEstimatedSave = async () => {
    if (!isEstimatedDisabled && validateScheduleChange()) {
      await removeZeroEstimatedSchedules();
      const filteredEstimatedTimes = getFilteredEstimatedTimes();
      if (
        Object.keys(filteredEstimatedTimes).length > 0 &&
        hasEstimatedChanges(filteredEstimatedTimes, item)
      ) {
        await saveEstimatedTimes(item, filteredEstimatedTimes);
      }
    }
  };

  const handleRealTimeToggle = () => {
    setIsRealTimeDisabled(!isRealTimeDisabled);
  };

  const handleRealTimeSave = async () => {
    if (!isRealTimeDisabled) {
      const payload = Object.entries(editedRealTimes).map(([id, realTime]) => ({
        id,
        realTime: realTime.toString(),
      }));
      if (payload.length > 0 && hasRealTimeChanges(editedRealTimes, item)) {
        await toast.promise(
          registerTimeAssignment(payload),
          {
            pending: "Guardando tiempo real invertido",
            success: "Tiempo real invertido guardado exitosamente",
            error: "Error guardando tiempo real invertido",
          }
        );
      }
    }
  };

  const changeDisabled = async (): Promise<void> => {
    // --- TIEMPOS ESTIMADOS ---
    if (canEditEstimated) {
      handleEstimatedToggle();
      await handleEstimatedSave();
    } else if (username === item?.username) {
      toast.info("No tienes permisos para editar los tiempos estimados.");
    }

    // --- TIEMPOS REALES ---
    if (canEditReal) {
      handleRealTimeToggle();
      await handleRealTimeSave();
    } else {
      toast.info("No tienes permisos para editar los tiempos reales.");
    }
  };

  const handleCollapseItem = (): void => {
    setActive(!active);
    setOpenDescription(!active);
    if (style === assignmentsStyles["assignment-item__hide-div"]) {
      setStyle(assignmentsStyles["assignment-item__show"]);
      setArrow(arrowup);
    } else {
      setStyle(assignmentsStyles["assignment-item__hide-div"]);
      setArrow(arrowdown);
    }
  };

  const handleAssignUser = async (task: ITask) => {
    const formData = {
      taskId: task.id,
      taskTitle: task.title,
      username: item?.username,
      fullName: `${item?.fullName}`,
      startDate: task.startDate || null,
      endDate: task.endDate || null,
    }

    try {
      await toast.promise(
        createAssignment(formData),
        {
          pending: "Asignando usuario...",
          success: "Usuario asignado exsitosamente",
          error: "Error al asignar el usuario a la tarea"
        }
      )
      getInfo();
    } catch (error) {
      toast.error("Error al asignar el usuario a la tarea");
      handleAxiosError(error, "Error al asignar el usuario a la tarea");
    }
  };


  const addNewTask = async (form: IFormData): Promise<void> => {
    try {
      await toast.promise(
        createTask({
          ...form,
        }, {
          onSuccess: (data) => {
            console.log("Tarea creada con éxito", data?.data?.data);
            handleAssignUser(data?.data?.data);
            cancelFunction();
          },
          onError: (error) => {
            console.error("Error al crear la tarea:", error);
          }
        }),
        {
          pending: "Creando tarea",
          success: "Tarea creada con éxito",
          error: "Error al crear la tarea"
        }
      )
    } catch (error) {
      console.error("Error al crear la tarea:", error);
    }
  };

  const openAddTaskModal = (): void => {
    setAddTaskIcon(!addTaskIcon);
    setStyle2(assignmentsStyles["assignment-item__show"]);
  };

  const cancelFunction = (): void => {
    addTaskRef.current?.resetForm();
    setStyle2(assignmentsStyles["assignment-item__hide-div"]);
    setAddTaskIcon(false);
  };
  // const syncUserInfo = async (): Promise<void> => {
  //   setItemLoader(true);
  //   /* Original commented code suggesting an axios call is removed */
  //   // Assuming getInfo() handles the data fetching previously done by the commented axios call
  //   setItemLoader(false); // Loader turned off immediately based on original code flow after commented out call
  //   getInfo(); // This function should presumably fetch updated item data
  // };

  return (
    <div className={assignmentsStyles["assignment-item"]}>
      <AssignmentCalendarBody
        item={item}
        active={active}
        arrow={arrow}
        handleCollapseItem={handleCollapseItem}
        changeDisabled={changeDisabled}
        isAdmin={role === "ADMIN"}
        editTrue={username === item?.username || role === "ADMIN"}
        isRealTimeDisabled={isRealTimeDisabled}
        addTaskIcon={addTaskIcon}
        openDescription={openDescription}
        openAddTaskModal={openAddTaskModal}
        closeAddTaskModal={cancelFunction}
        week={week}
      />
      <div className={style2}>
        <AddTask
          ref={addTaskRef}
          addNewTask={addNewTask}
          closeAddTask={cancelFunction}
          sections={sections?.data}
          projects={projects?.data}
        />
      </div>
      <div className={style}>
        {item?.assignments?.map((assignment: Assignment, idx: number) => (
          <AssignmentCalendarTask
            weekDays={week}
            key={assignment.task_assignment_id || `assignment-${idx}`}
            assignment={assignment}
            // days={item.hoursWeek}
            isEstimatedDisabled={isEstimatedDisabled}
            isRealTimeDisabled={isRealTimeDisabled}
            // assigned={item.email}
            // allUsers={allUsers}
            actionShowTaskDetail={() => actionShowTaskDetail(item, assignment)}
            estimatedTimes={editedEstimatedTimes[assignment.task_assignment_id] || {}}
            onEstimatedChange={(date, value) => handleEstimatedChange(assignment.task_assignment_id, date, value)}
            setEditedRealTimes={setEditedRealTimes}
            validateHourInput={validateHourInput}
          />
        ))}
      </div>
    </div >
  )
}
